import { z } from 'zod';
import { POSITION_SOURCES } from './sync-position.schemas';

/** A truck's newest fix, as the live map draws it. Times are ISO strings: this crosses the wire, on the snapshot and on the stream. */
export const liveTruckFixSchema = z.object({
  source: z.enum(POSITION_SOURCES),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  speedMps: z.number().nullable(),
  headingDeg: z.number().nullable(),
  deviceTime: z.iso.datetime(),
});
export type LiveTruckFix = z.infer<typeof liveTruckFixSchema>;

/** The trip a truck is on and what the map filters by: its customer and its route (the trip's origin and destination zone codes). */
export const liveTruckTripSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.string(),
  customerId: z.uuid(),
  customerName: z.string(),
  originZoneCode: z.string().nullable(),
  destinationZoneCode: z.string().nullable(),
});
export type LiveTruckTrip = z.infer<typeof liveTruckTripSchema>;

/** One truck on the live map (`GET /tracking/live-trucks`): where it was last seen, and the trip it is on, if any. */
export const liveTruckSchema = z.object({
  truckId: z.uuid(),
  registration: z.string(),
  fix: liveTruckFixSchema,
  trip: liveTruckTripSchema.nullable(),
});
export type LiveTruck = z.infer<typeof liveTruckSchema>;

/** One truck's new fix on `GET /tracking/stream`. Carries no trip: that comes from the snapshot. */
export const positionFeedEntrySchema = liveTruckFixSchema.extend({ truckId: z.uuid() });
export type PositionFeedEntry = z.infer<typeof positionFeedEntrySchema>;

/** What the stream sends as a `positions` event: the newest fix of every truck that reported since the last one, at most one per truck. */
export const positionFeedEventSchema = z.object({ positions: z.array(positionFeedEntrySchema) });
export type PositionFeedEvent = z.infer<typeof positionFeedEventSchema>;
